import React, { useEffect, useState } from "react";
import { Badge, Button, Form, InputGroup, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { tmrPushEvent } from "@components/Counters";
import { DevForm } from "@components/Forms/Fields";
import { Awesome } from "@components/Helpers";
import { parseQS, url_reverse } from "@components/Utils";
import { getAuthHost } from "@vkplay/shared";

const setCookie = (name, value, { path, expires, domain }) => {
    document.cookie = `${name}=${encodeURIComponent(
        value
    )}; path=${path}; expires=${expires.toUTCString()}; domain=${domain}`;
};

export const useDesktopVersion = () => {
    const { t } = useTranslation();
    const desktopVersionKey = "devarea_show_desktop_version";
    const [showDesktopVersion, setShowDesktopVersion] = useState(sessionStorage.getItem(desktopVersionKey));

    const setDesktopVersion = () => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
            viewport.setAttribute("content", "width=1024");
            sessionStorage.setItem(desktopVersionKey, true);
            setShowDesktopVersion(true);
        }
    };

    const setMobileVersion = () => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
            viewport.setAttribute("content", "width=device-width, initial-scale=1");
            sessionStorage.removeItem(desktopVersionKey);
            setShowDesktopVersion(false);
        }
    };

    const showDesktopVersionLink = (reload) => {
        return (
            <a
                href="#"
                onClick={(ev) => {
                    ev.preventDefault();
                    if (reload) {
                        sessionStorage.setItem(desktopVersionKey, true);
                        location.reload();
                    } else {
                        setDesktopVersion();
                    }
                }}
            >
                {t("Открыть ПК-версию")}
            </a>
        );
    };

    useEffect(() => {
        if (showDesktopVersion) {
            setDesktopVersion();
        } else {
            setMobileVersion();
        }
    }, [showDesktopVersion]);

    return { showDesktopVersionLink, showDesktopVersion, setMobileVersion };
};

export function NavigationBar({ commonInfo }) {
    const { t } = useTranslation();
    const { account } = commonInfo;
    const getParams = parseQS();
    const [quickSearch, setQuickSearch] = useState(getParams.quick_search || "");
    const { showDesktopVersionLink, showDesktopVersion, setMobileVersion } = useDesktopVersion();

    const changeLang = (lang) => {
        const expires = new Date();
        const { host } = window.location;
        const domain = host.endsWith("gm2.corp.mail.ru")
            ? ".gm2.corp.mail.ru"
            : host.endsWith(".dev-vkplay.ru")
              ? ".dev-vkplay.ru"
              : ".vkplay.ru";
        expires.setDate(Date.now() + 365 * 24 * 60);
        setCookie("amc_lang", lang[1], {
            path: "/",
            expires,
            domain,
        });

        window.location.reload();
    };

    return (
        <Navbar bg="light" expand="lg">
            <Navbar.Brand href={url_reverse("games:list")}>
                <img src="/showcasestatic/img/logo-vkplay.png" style={{ height: "40px", marginTop: "-5px" }} />
            </Navbar.Brand>

            <Navbar.Toggle aria-controls="navbarNavAltMarkup" />

            <Navbar.Collapse id="navbarNavAltMarkup">
                <NavigationMenu
                    commonInfo={commonInfo}
                    showDesktopVersion={showDesktopVersion}
                    setMobileVersion={setMobileVersion}
                />

                {account && (
                    <Form className="my-2 my-lg-0" action={url_reverse("games:list")} method="GET">
                        <InputGroup style={{ minWidth: "150px" }}>
                            <DevForm.TextInput
                                name="quick_search"
                                placeholder={t("Название или ID игры")}
                                value={quickSearch}
                                onChange={(ev) => setQuickSearch(ev.target.value)}
                                // Ограничение длины, чтобы не получить 414 (URI Too Long) от веб-сервера
                                maxLength={256}
                            />
                            <InputGroup.Append>
                                <Button variant="outline-secondary" type="submit">
                                    <Awesome type="search" />
                                </Button>
                            </InputGroup.Append>
                        </InputGroup>
                    </Form>
                )}

                <Nav className="ml-auto">
                    <NavDropdown
                        className="lang-dropdown"
                        title={<span className={`dev-country dev-country-${commonInfo.language_data?.current}`}></span>}
                    >
                        {commonInfo.language_data?.available.map((object, i) => {
                            if (commonInfo.language_data.current === object[0]) {
                                return null;
                            }
                            return (
                                <a
                                    className="dropdown-item"
                                    href="#"
                                    key={`lang${i}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        changeLang(object);
                                    }}
                                >
                                    <span className={`dev-country dev-country-${object[0]}`}></span>
                                    &nbsp;
                                    {object[2]}
                                </a>
                            );
                        })}
                    </NavDropdown>

                    {account ? (
                        <>
                            <ChatNavBtn commonInfo={commonInfo} />
                            <span style={{ height: "40px" }}>
                                <img
                                    className="rounded-circle mx-2"
                                    style={{ height: "40px" }}
                                    src={`//avatar.vkplay.ru/avatar/${commonInfo.auth_info.user_id}.45.jpeg`}
                                />
                            </span>
                            <NavDropdown title={account.login} className="fs08rem">
                                <NavDropdown.Item
                                    href={`https://${getAuthHost("auth-ac")}/logout?continue=https%3A//${
                                        commonInfo.domain_devs
                                    }`}
                                    referrerPolicy="origin"
                                >
                                    {t("Выход")}
                                </NavDropdown.Item>
                            </NavDropdown>
                            <div className="d-block d-md-none">{showDesktopVersionLink()}</div>
                        </>
                    ) : (
                        <a
                            className="nav-link"
                            href={`https://${getAuthHost("auth-ac")}/logout?continue=https%3A//${
                                commonInfo.domain_devs
                            }`}
                        >
                            {t("Выход")}
                        </a>
                    )}
                </Nav>
            </Navbar.Collapse>
        </Navbar>
    );
}

function NavigationMenu({ commonInfo, showDesktopVersion, setMobileVersion }) {
    const { t } = useTranslation();
    const {
        account,
        game_moderation_count,
        utd_moderation_count,
        serials_moderation_count,
        project_transfer_moderation_count,
        featuring_moderation_count,
        basic_promo_moderation_count,
        event_moderation_count,
        pr_moderation_count,
    } = commonInfo;

    const admin_management = [];
    if (account?.is_super || account?.moderator || account?.pr_moderator) {
        admin_management.push({
            url: url_reverse("devs:dashboard", { dash_type: "developers" }),
            label: t("Дашборд компаний"),
        });
    }
    if (account?.is_super) {
        admin_management.push({ url: url_reverse("admin:account:list"), label: t("Аккаунты") });
        admin_management.push({
            url: url_reverse("admin:actionlog:list", { kind: "default" }),
            label: t("Лог действий"),
        });
    }
    if (account?.is_super || account?.pr_moderator) {
        admin_management.push({ url: url_reverse("admin:publisher:list"), label: t("Издатели и Разработчики") });
    }
    if (account?.is_super || account?.moderator || account?.pr_moderator || account?.read_catalog) {
        admin_management.push({ url: url_reverse("admin:upcoming_releases:list"), label: t("Ближайшие релизы") });
    }

    const admin_moderation = [];
    if (account?.is_super) {
        admin_moderation.push({
            url: url_reverse("admin:moderation:game:list"),
            label: t("Игры (постмодерация)"),
            badge: game_moderation_count,
        });
        admin_moderation.push({
            url: url_reverse("admin:moderation:featuring:list", { kind: "promo" }),
            label: t("Акции"),
            badge: featuring_moderation_count,
        });
        admin_moderation.push({
            url: url_reverse("admin:moderation:featuring:list", { kind: "launch" }),
            label: t("Базовое промо"),
            badge: basic_promo_moderation_count,
        });
        admin_moderation.push({
            url: url_reverse("admin:moderation:event:list"),
            label: t("Ивенты"),
            badge: event_moderation_count,
        });
        admin_moderation.push({
            url: url_reverse("admin:moderation:market_serial:list"),
            label: t("Ключи"),
            badge: serials_moderation_count,
        });
    }
    if (account?.is_super || account?.pr_moderator) {
        admin_moderation.push({
            url: url_reverse("admin:moderation:pr:list"),
            label: t("Пиар"),
            badge: pr_moderation_count,
        });
    }
    if (account?.is_super) {
        admin_moderation.push({
            url: url_reverse("admin:moderation:documents:list"),
            label: t("УПД"),
            badge: utd_moderation_count,
        });
        admin_moderation.push({
            url: url_reverse("admin:moderation:project_transfer:list", { kind: "moder_fin" }),
            label: t("Передача проектов"),
            badge: project_transfer_moderation_count,
        });
    }

    const admin_settings = [];
    if (account?.is_super) {
        admin_settings.push({ url: url_reverse("pocket:list"), label: t("Карманы") });
        admin_settings.push({ url: url_reverse("admin:event:list"), label: t("Ивенты") });
        admin_settings.push({ url: url_reverse("admin:tag:list"), label: t("Теги") });
        admin_settings.push({
            label: t("ИЦ"),
            submenu: [
                {
                    label: t("Доступы к test.dl.astrum.team"),
                    url: url_reverse("admin:gamecenter:access_test_astrum:list"),
                },
                {
                    label: t("Доступы к admindl.vkplay.ru"),
                    url: url_reverse("admin:gamecenter:access_admin:list"),
                },
            ],
        });
        admin_settings.push({ url: url_reverse("admin:notice:list"), label: t("Уведомления") });
    }

    const admin_reports = [];
    if (account?.is_super || account?.read_catalog) {
        admin_reports.push({
            label: t("Статистика"),
            submenu: [
                {
                    label: t("Игры"),
                    url: url_reverse("statements:games_report"),
                },
                {
                    label: t("Разработчики"),
                    url: url_reverse("statements:developers_report"),
                },
            ],
        });
    }
    if (account?.is_super) {
        admin_reports.push({ url: url_reverse("admin:developers_email"), label: t("Email разработчиков") });
    }

    let admin_menu = [];
    if (admin_management.length) {
        admin_management.unshift({ header: t("Управление") });
        admin_menu = admin_menu.concat(admin_management);
    }
    if (admin_moderation.length) {
        if (admin_menu.length) {
            admin_menu.push({ divider: true });
        }
        admin_moderation.unshift({ header: t("Модерация") });
        admin_menu = admin_menu.concat(admin_moderation);
    }
    if (admin_settings.length) {
        if (admin_menu.length) {
            admin_menu.push({ divider: true });
        }
        admin_settings.unshift({ header: t("Настройка") });
        admin_menu = admin_menu.concat(admin_settings);
    }
    if (admin_reports.length) {
        if (admin_menu.length) {
            admin_menu.push({ divider: true });
        }
        admin_reports.unshift({ header: t("Выгрузка") });
        admin_menu = admin_menu.concat(admin_reports);
    }

    return (
        <Nav className="mr-5">
            {(account?.is_super || account?.moderator || account?.pr_moderator || account?.read_catalog) && (
                <NavDropdown title={t("Администрирование")}>
                    {admin_menu.map((m, ind) => {
                        if (m.header) {
                            return (
                                <NavDropdown.Item key={"header" + ind} disabled>
                                    {m.header}
                                </NavDropdown.Item>
                            );
                        }
                        if (m.divider) {
                            return <NavDropdown.Divider key={"divider" + ind} />;
                        }
                        if (m.submenu) {
                            return (
                                <React.Fragment key={"submenu" + ind}>
                                    <NavDropdown.Item className="submenu">{m.label}</NavDropdown.Item>
                                    <div className="dropdown-menu">
                                        {m.submenu.map((sm) => {
                                            return (
                                                <NavDropdown.Item key={sm.label} as={Link} to={sm.url}>
                                                    {sm.label}
                                                </NavDropdown.Item>
                                            );
                                        })}
                                    </div>
                                </React.Fragment>
                            );
                        }
                        const cn = m.badge === undefined ? "" : "d-flex align-items-center";
                        return (
                            <NavDropdown.Item key={"menu" + ind} as={Link} to={m.url} className={cn}>
                                {m.label}
                                {m.badge !== undefined && (
                                    <>
                                        {" "}
                                        <Badge className="ml-auto" variant="info">
                                            {m.badge}
                                        </Badge>
                                    </>
                                )}
                            </NavDropdown.Item>
                        );
                    })}
                </NavDropdown>
            )}

            {(account?.own_developers || account?.pr_moderator) && (
                <Nav.Link as={Link} to={url_reverse("devs:list")}>
                    {t("Компании")}
                </Nav.Link>
            )}

            {account && (
                <Nav.Link as={Link} to={url_reverse("games:list")}>
                    {t("Игры")}
                </Nav.Link>
            )}

            {!account?.is_super && account?.access_to_pockets && (
                <Nav.Link href={url_reverse("pocket:list")}>{t("Карманы")}</Nav.Link>
            )}

            {(account?.is_super || account?.support) && (
                <NavDropdown title={t("Магазин")}>
                    <NavDropdown.Item as={Link} to={url_reverse("market:order:list")}>
                        {t("Заказы")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("market:error:list")}>
                        {t("Заказы с ошибками")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("partner:payment:list")}>
                        {t("Партнерские платежи")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("market:merchant_set:list")}>
                        {t("Наборы мерчантов")}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.Item as={Link} to={url_reverse("market:foreign_invoice:list")}>
                        {t("Пополнение Steam")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("market:steam_gift_order:list")}>
                        {t("Покупки через Steam gift bot")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("market:ckassa_blacklist:list")}>
                        {t("Блэклист ckassa")}
                    </NavDropdown.Item>
                    {account.is_super && (
                        <>
                            <NavDropdown.Divider />
                            <NavDropdown.Item as={Link} to={url_reverse("market:sale:list")}>
                                {t("Активные скидки")}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={url_reverse("market:promocode:list")}>
                                {t("Промокоды")}
                            </NavDropdown.Item>
                            <NavDropdown.Divider />
                            <NavDropdown.Item as={Link} to={url_reverse("market:serial:list")}>
                                {t("Ключи")}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={url_reverse("market:platform:list")}>
                                {t("Платформы")}
                            </NavDropdown.Item>
                            <NavDropdown.Divider />
                            <NavDropdown.Item as={Link} to={url_reverse("market:buka")}>
                                {t("Каталог Бука")}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to={url_reverse("market:enaza")}>
                                {t("Каталог Enaza")}
                            </NavDropdown.Item>
                        </>
                    )}
                </NavDropdown>
            )}

            {(account?.is_super || account?.moderator) && (
                <NavDropdown title={t("Модерация")}>
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:comments")}>
                        {t("Комментарии")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:community")}>
                        {t("Коммьюнити")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:chat")}>
                        VK Play Chat
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:profile_posts")}>
                        {t("Посты в профиле")}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:microreview:list")}>
                        {t("Отзывы")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("moderate:microreview:complaints")}>
                        {t("Жалобы на отзывы")}
                    </NavDropdown.Item>
                </NavDropdown>
            )}

            {(account?.is_super || account?.read_catalog) && (
                <NavDropdown title={t("Фин. отчетность")}>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:month")}>
                        {t("Ежемесячные отчеты")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:dmr")}>
                        {t("Отчет по данным ДМР")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:payments_control")}>
                        {t("Контроль оплат")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:royalty")}>
                        {t("Отчет Агента")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:developer_receipts")}>
                        {t("Отчет по чекам разработчиков")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:documents")}>
                        {t("Документы на подпись")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:report_cloud:list")}>
                        {t("Отчеты в облаке")}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to={url_reverse("finstatements:merchants")}>
                        {t("Список мерчантов")}
                    </NavDropdown.Item>
                </NavDropdown>
            )}

            <Nav.Link href="https://documentation.vkplay.ru/" target="_blank">
                {t("Документация")}
            </Nav.Link>

            {showDesktopVersion && (
                <Nav.Link
                    href="#"
                    onClick={(ev) => {
                        ev.preventDefault();
                        setMobileVersion();
                    }}
                >
                    {t("Открыть мобильную версию")}
                </Nav.Link>
            )}
        </Nav>
    );
}

const ChatNavBtn = ({ commonInfo }) => {
    const notif = false;

    // TODO: убрать ненужное или после переезда на макс сделать аналог

    // const [notif, setNotif] = useState(false);
    // const { ext_api_get } = useRequest();

    // const getChatCounter = function () {
    //     ext_api_get(null, "/messenger/v1/user/messages/has_unread", {
    //         then: (resp) => {
    //             setNotif(Boolean(resp.is_unread));
    //         },
    //     });
    // };

    // useEffect(() => {
    //     getChatCounter();
    //     setInterval(getChatCounter, 60 * 1000); // get new data every 1min
    // }, []);

    const openChat = (ev) => {
        ev.preventDefault();
        tmrPushEvent("goto_chat_click", "header", commonInfo.auth_info.user_id);
        window.open(commonInfo.chat_url, "_blank");
    };

    return (
        <span className="navbar-chat">
            {/* <a href={`//${dom}/chat`} target="_blank" className="navbar-chat__btn">
                <span className="navbar-chat__icon"></span>
                <span className={`navbar-chat__notif ${notif ? "is-shown" : ""}`}></span>
            </a> */}
            <a onClick={openChat} href="#" className="navbar-chat__btn">
                <span className="navbar-chat__icon"></span>
                <span className={`navbar-chat__notif ${notif ? "is-shown" : ""}`}></span>
            </a>
        </span>
    );
};
